import { RouteLocationNormalized } from 'vue-router';
import { Ref, ComputedRef, Component } from 'vue';
import { AssortmentType, IpType, ContactGender } from '@harpoon/ValueObjects';
import { NotificationType } from '@/generated';

export { AssortmentType, ContactGender, IpType, NotificationType };

// To improve the expressiveness of the names, this interface could probably better simply be called 'Page',
// so the property 'page' could be renamed to 'number' and 'pageSize' simply to 'size'
export interface PageInformation {
    page: number;
    pageSize?: number;
}

interface PaginationLinks {
    first: string;
    last: string;
    prev: string;
    next: string;
}
interface PaginationMetaLinks {
    url: string | null;
    label: string;
    active: boolean;
}
export interface PaginationMeta {
    current_page: number;
    from: number;
    last_page: number;
    links: PaginationMetaLinks[];
    path: string;
    per_page: number;
    to: number;
    total: number;
}
export interface PaginatedResponse {
    data: unknown[];
    links: PaginationLinks;
    meta: PaginationMeta;
}

export interface ObjectResponse {
    data?: unknown;
    meta?: unknown;
    status?: unknown;
    errors?: unknown;
    message?: string;
}

export enum OrderType {
    Dedicated = 'dedicated',
    Rent = 'rent',
    Colocation = 'colocation',
    Service = 'service',
    Cloudserver = 'cloudserver',
    Ip = 'ip',
    S3 = 's3',
    Domain = 'domain_robot',
}

export enum OrderStatus {
    Draft = 'draft',
    Completed = 'completed',
    ApprovedByCustomer = 'approved_by_customer',
    Approved = 'approved',
    Due = 'due',
    Active = 'active',
    Canceled = 'canceled',
    Terminated = 'terminated',
    Rejected = 'rejected',
}

export enum TableCellType {
    Normal = 'normal',
    Actions = 'actions',
}

export enum PaginationLinkType {
    Normal = 'normal',
    Disabled = 'disabled',
    Current = 'current',
}

export enum SortDirection {
    Asc = 'asc',
    Desc = 'desc',
}

export enum DateTimeFormat {
    PrettyWithTime = 'prettyWithTime',
    Pretty = 'pretty',
    Short = 'short',
}

export type GermanyTimeZoneKey = 'cet' | 'cest';

export interface Attribute {
    name: string;
    label?: string;
    isSortable: boolean;
    isSearchable: boolean;
    position: number;
    columnClass?: string;
    sortField?: string;
}

export enum TooltipPosition {
    Left = 'left',
    Right = 'right',
    Bottom = 'bottom',
}

export interface DescriptionListItem {
    title: string;
    data: unknown;
}

export enum Functional {
    Success = 'success',
    Info = 'info',
    Warning = 'warning',
    Error = 'error',
}

export interface Address {
    id: number;
    street: string;
    number?: string;
    additional?: string;
    zip?: string;
    city: string;
    country: Country;
}
export interface BankAccount {
    bank_name: string;
    iban: string;
    bic: string;
}
export interface Contact {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    is_active: boolean;
    roles: ContactRoles;
    permissions: ContactPermissions;
    title?: string | null;
    gender?: ContactGender | null;
    phone?: string | null;
    mobile?: string | null;
}

export enum PaymentType {
    BankTransfer = 'bank_transfer',
    DirectDebit = 'direct_debit',
    Paypal = 'paypal',
}

export interface Customer {
    number: number;
    name: string;
    vat_id: string;
    payment_type: PaymentType | string;
    payment_due_days: number;
    receipt_mail_address: string;
    address?: Address;
    bankAccounts: BankAccount[];
    contacts: Contact[];
}

export enum InvoiceStatus {
    Open = 'open',
    Paid = 'paid',
}

export enum InvoiceType {
    Invoice = 'invoice',
    CreditNote = 'credit_note',
}

export enum Theme {
    None = 'none',
    Normal = 'normal',
    Primary = 'primary',
    Secondary = 'secondary',
    Tertiary = 'tertiary',
    Cta = 'cta',
    Danger = 'danger',
    Disabled = 'disabled',
    Success = 'success',
    Warning = 'warning',
    Light = 'light',
    Dark = 'dark',
}

/** Theme subset for status badges (semantic colors only). */
export enum BadgeTheme {
    Primary = 'primary',
    Secondary = 'secondary',
    Danger = 'danger',
    Cta = 'cta',
    Warning = 'warning',
    Success = 'success',
}

export enum LightDarkTheme {
    Light = 'light',
    Dark = 'dark',
}

export enum FormTheme {
    Website = 'website',
    CustomerArea = 'customer_area',
}

export enum AlertTheme {
    Solid = 'solid',
    Outline = 'outline',
}

export enum FeedCommentTheme {
    Customer = 'customer',
    Agent = 'agent',
}

export enum TicketContentType {
    Html = 'text/html',
    Plain = 'text/plain',
}

export enum TicketUserRole {
    Customer = 'Customer',
    Agent = 'Agent',
    Admin = 'Admin',
}

export interface TicketUser {
    id: number;
    name: string;
    avatar_base64: string | null;
    role: TicketUserRole;
}

export interface TicketAttachment {
    id: number;
    filename: string;
    size: number;
    content_type: string;
}

export interface TicketAttachmentFile {
    content_base64: string;
    content_type: string;
    name: string;
}

export interface TicketComment {
    id: number;
    attachments: TicketAttachment[];
    content_type: TicketContentType;
    body: string;
    author: TicketUser | null;
    created_at: string;
    public: boolean;
}

export enum TicketStatus {
    Open = 'open',
    Pending = 'pending',
    Solved = 'solved',
    Closed = 'closed',
}

export enum TicketFetchingSourceType {
    Dashboard = 'dashboard',
    Index = 'index',
}

export interface TicketTopic {
    key: string;
    name_i18n: string;
    name: string;
    is_internal: boolean;
}

export interface Ticket {
    id: number;
    number: number;
    subject: string;
    requester: TicketUser;
    comments: TicketComment[];
    created_at: string;
    updated_at: string;
    status: TicketStatus;
}

export interface Invoice {
    number: string;
    status: InvoiceStatus | string;
    date: string | null;
    due_on: string | null;
    total_net: string | null;
    total_gross: string | null;
    type: InvoiceType;
}

export enum IconKey {
    Extern = 'extern',
}

export interface ActionItem {
    name: string;
    onClick: () => void;
    iconType?: IconType;
}

export enum FileType {
    Pdf = 'application/pdf',
    Csv = 'text/csv',
}

export enum Size {
    None,
    Small,
    Normal,
    Large,
}

export enum SizeAll {
    Tiny,
    Small,
    Normal,
    Large,
    Huge,
}

export interface File {
    name: string;
    type: FileType;
    size: number;
    base64Content: string;
}

export interface TestItem {
    id: number;
    name: string;
    activity_start?: string;
}

export interface OptionItem {
    id: string;
    name: string;
}

export interface TableItem {
    resource:
        | Order
        | Invoice
        | IpUsage
        | RentUsage
        | Ticket
        | Customer
        | ColocationUsage
        | DeviceUsage
        | TestItem
        | NetworkIp
        | Contact
        | DatacenterVisit
        | InboxNotification;
    notifications?: string[];
}

export enum ToastType {
    Success = 'success',
    Info = 'info',
    Warning = 'warning',
    Error = 'error',
}
export interface Toast {
    key: string;
    title: string;
    text: string;
    type: ToastType;
    isVisible: boolean;
}

export interface Country {
    id: number;
    code: string;
    name: string;
    is_dach: boolean;
    payment_term?: {
        payment_due_days: number;
        discounts: Array<{ days: number; percentage: number }>;
    };
    payment_type?: PaymentType;
}

export enum TextFieldType {
    Text = 'text',
    Email = 'email',
    Password = 'password',
    Number = 'number',
    Search = 'search',
    Tel = 'tel',
    Url = 'url',
    Checkbox = 'checkbox',
    File = 'file',
}

export enum OrderItemCategory {
    Device = 'device',
    Ram = 'ram',
    Storage = 'storage',
    Gpu = 'gpu',
    Network = 'network',
    Service = 'service',
    Domain = 'domain_robot',
    Ip = 'ip',
    S3 = 's3',
    Colocation = 'colocation',
    Power = 'power',
    Cloudserver = 'cloudserver',
    CloudserverResource = 'cloudserver_resource',
    Locker = 'locker',
    Peripheral = 'peripheral',
    Backup = 'backup',
    Unknown = 'unknown',
}

export enum OrderItemType {
    CloudserverItem = 'order_cloudserver_resource_item',
    ColocationItem = 'order_colocation_item',
    CombinedTrafficItem = 'order_combined_traffic_item',
    DeviceComponentItem = 'order_device_component_item',
    DeviceItem = 'order_device_item',
    IpItem = 'order_ip_item',
    PowerItem = 'order_power_item',
    FeedItem = 'order_feed_item',
    ServiceItem = 'order_service_item',
    TrafficItem = 'order_traffic_item',
    UplinkItem = 'order_uplink_item',
    CrossConnectItem = 'order_cross_connect_item',
    VpnItem = 'order_vpn_item',
    CloudItem = 'order_cloud_item',
    BackupItem = 'order_backup_item',
}

export enum UplinkType {
    Wan = 'wan',
    Lan = 'lan',
}

export enum VpnType {
    User = 'user',
    Ip = 'ip',
}

export interface OrderItemPricing {
    price_per_unit: number;
    units_included_monthly: number;
}

export interface DeviceUnit {
    name: string[];
}

export interface OrderDeviceItemExtra {
    device_usage: DeviceUsage;
    device_unit: DeviceUnit;
    cpu_name: string;
    cpu_cores: number;
    cpu_threads: number;
    cpu_frequency: number;
    rack_name: string;
}

export interface OrderColocationItemExtra {
    colocation_usage: ColocationUsage;
    rack_name: string;
    rack_room: string;
}
export interface OrderVpnItemExtra {
    vpn_type: VpnType;
    ips?: Array<string>;
}

export interface OrderCloudItemExtra {
    account_name: string;
    domain_name: string;
}

export interface OrderPeripheralItemExtra {
    device_type_name: string;
}

export interface OrderBackupItemExtra {
    user_login?: string;
    pricing: {
        device: number;
        storage: number;
        booster: number;
    };
}

export interface OrderDeviceItemGraph {
    graph_from: string;
    graph_to: string;
    graph_as_svg: string;
}

export interface OrderColocationItemGraph {
    graph_from: string;
    graph_to: string;
    graph_as_svg: string;
}

export interface OrderTrafficItemGraph {
    graph_from: string;
    graph_to: string;
    graph_as_svg: string;
}

export interface FetchOrderDeviceItemGraphMeta {
    from_min: Date;
    from: Date;
    to: Date;
    to_max: Date;
}

export interface FetchOrderColocationItemGraphMeta {
    from_min: Date;
    from: Date;
    to: Date;
    to_max: Date;
}

export interface FetchOrderFeedItemPowerGraphMeta {
    from_min: Date;
    from: Date;
    to: Date;
    to_max: Date;
}

export interface FetchOrderTrafficItemMeta {
    from_min: Date;
    from: Date;
    to: Date;
    to_max: Date;
}

export interface OrderItem {
    id: number;
    category: OrderItemCategory;
    description: string;
    type: OrderItemType;
    consumption_discount_percent: number;
    items?: unknown[];
    pricing?: OrderItemPricing;
    extra_data?:
        | OrderDeviceItemExtra
        | OrderColocationItemExtra
        | OrderVpnItemExtra
        | OrderCloudItemExtra
        | OrderPeripheralItemExtra
        | OrderS3ItemExtra
        | OrderBackupItemExtra;
}

export interface UpdateS3PasswordValidationError {
    password: string[];
    password_confirmation: string[];
}

export interface UpdateBackupPasswordValidationError {
    password: string[];
    password_confirmation: string[];
}

export interface CreateTicketValidationError {
    topic: string[];
    subject: string[];
    attachments?: string[];
}

export interface CreateCommentValidationError {
    body: string[];
    attachments: string[];
}

export interface FetchOrderDeviceItemGraphsValidationError {
    from: string[];
    to: string[];
}

export interface FetchOrderColocationItemGraphsValidationError {
    from: string[];
    to: string[];
}

export interface FetchOrderTrafficItemGraphsValidationError {
    from: string[];
    to: string[];
}

export interface FetchPaginatedTicketsValidationError {
    from: string[];
    to: string[];
}

export interface FetchOrderFeedItemPowerGraphValidationError {
    from: string[];
    to: string[];
}

export type FetchPaginatedAttacksValidationError = Record<string, never>;

export type IndexValidationError = {
    search: string[];
};

export type FetchPaginatedOrdersValidationError = IndexValidationError;

export type FetchPaginatedInvoicesValidationError = IndexValidationError;

export type FetchPaginatedIpUsageAddressesValidationError = IndexValidationError;

export type FetchPaginatedIpUsageNetworksValidationError = IndexValidationError;

export type FetchPaginatedNetworkIpsValidationError = IndexValidationError;

export type FetchPaginatedDeviceUsagesValidationError = IndexValidationError;

export type FetchPaginatedRentUsagesValidationError = IndexValidationError;

export type FetchPaginatedColocationUsagesValidationError = IndexValidationError;

export type FetchPaginatedVpnIpUsagesValidationError = IndexValidationError;

export type FetchPaginatedDatacenterVisitsValidationError = IndexValidationError;

export type FetchPaginatedInboxNotificationsValidationError = IndexValidationError;

export interface CreateDatacenterVisitValidationError {
    contact_id?: string[];
    customer_id?: string[];
    visit_start?: string[];
    visit_end?: string[];
    visitor_names?: string[];
    rack_names?: string[];
    notes?: string[];
    is_noc?: string[];
}

export interface Interval {
    interval_number: number;
    interval_unit: string;
}
export interface Order {
    number: string | object;
    customer_reference: string | null;
    status: OrderStatus | string;
    type: OrderType | string;
    completed_at: string | null;
    approved_at: string | null;
    planned_start_at: string | null;
    start_at: string | null;
    end_at: string | null;
    setup_price: number;
    monthly_price: number;
    cancelation_period: Interval;
    extension_period: Interval;
    min_order_duration: Interval;
    items: OrderItem[];
}

export enum StepStatus {
    Complete = 'complete',
    Current = 'current',
    Incomplete = 'incomplete',
}

export interface NetworkIp {
    ip: string;
    dns_name: string;
}

export interface UpdateIpUsageNetworkValidationError {
    customer_reference?: string[];
}

export interface IpUsage {
    id: number;
    ip: string;
    order_number: string | object;
    ip_type: IpType;
    dns_name: string;
    network_ip_count: number;
    has_dns_management: boolean;
    customer_reference?: string;
}

export interface VpnIpUsage {
    id: number;
    ip: string;
    order_number: string | object;
}

export interface UpdateIpUsageAddressValidationError {
    dns_name?: string[];
    customer_reference?: string[];
}

export interface Cpu {
    name: string;
    cores: number;
    threads: number;
    frequency: number;
}

export interface ComponentUnitDescription {
    count: number;
    component_name: string;
}

export interface DeviceUnitComponentUnitDescription {
    component_type_name: string;
    component_unit_descriptions: ComponentUnitDescription[];
}

export interface UpdateDeviceUsageValidationError {
    order_device_item_customer_reference?: string[];
}

export interface UpdateColocationUsageValidationError {
    order_colocation_item_customer_reference?: string[];
}

export interface CreateContactValidationError {
    email: string[];
}

export interface UpdateContactValidationError {
    email: string[];
}

export interface DeviceUsage {
    id: number;
    device_name: string;
    device_unit_name: string;
    device_unit_old_name: string | null;
    device_unit_component_unit_descriptions: DeviceUnitComponentUnitDescription[];
    device_unit_netbox_item_rack_name: string;
    device_unit_netbox_item_rack_room: string;
    cpu: Cpu;
    order_number: string;
    order_device_item_id: number;
    order_device_item_customer_reference: number;
    order_traffic_item_ids: number[];
    has_hardware_management_api: boolean;
}

export enum HardwareManagementHealthState {
    OK = 'OK',
    Warning = 'Warning',
    Critical = 'Critical',
    Unknown = 'Unknown',
}

export enum HardwareManagementState {
    Enabled = 'Enabled',
    Disabled = 'Disabled',
    Offline = 'Offline',
    Absent = 'Absent',
    Starting = 'Starting',
    UnavailableOffline = 'UnavailableOffline',
}

export enum HardwareManagementPowerState {
    On = 'On',
    Off = 'Off',
    PoweringOn = 'PoweringOn',
    PoweringOff = 'PoweringOff',
    Paused = 'Paused',
}

export enum HardwareManagementIndicatorLedState {
    Lit = 'Lit',
    Off = 'Off',
    Blinking = 'Blinking',
}

export interface HardwareManagementInfo {
    bios_version: string;
    indicator_led: HardwareManagementIndicatorLedState;
    manufacturer: string;
    model: string;
    name: string;
    power_state: HardwareManagementPowerState;
    sku: string;
    serial_number: string;
    status_state: HardwareManagementState;
    status_health: HardwareManagementHealthState;
    memory_health: HardwareManagementHealthState | null;
    processor_health: HardwareManagementHealthState | null;
}

export enum HardwareManagementPhysicalContext {
    Room = 'Room',
    Intake = 'Intake',
    Exhaust = 'Exhaust',
    LiquidInlet = 'LiquidInlet',
    LiquidOutlet = 'LiquidOutlet',
    Front = 'Front',
    Back = 'Back',
    Upper = 'Upper',
    Lower = 'Lower',
    Left = 'Left',
    Right = 'Right',
    Chassis = 'Chassis',
    Backplane = 'Backplane',
    SystemBoard = 'SystemBoard',
    PowerSupply = 'PowerSupply',
    VoltageRegulator = 'VoltageRegulator',
    Rectifier = 'Rectifier',
    Transformer = 'Transformer',
    ACInput = 'ACInput',
    ACOutput = 'ACOutput',
    DCBus = 'DCBus',
    PowerSubsystem = 'PowerSubsystem',
    CPU = 'CPU',
    CPUSubsystem = 'CPUSubsystem',
    Memory = 'Memory',
    MemorySubsystem = 'MemorySubsystem',
    GPU = 'GPU',
    GPUSubsystem = 'GPUSubsystem',
    ASIC = 'ASIC',
    FPGA = 'FPGA',
    Accelerator = 'Accelerator',
    StorageDevice = 'StorageDevice',
    NetworkingDevice = 'NetworkingDevice',
    Transceiver = 'Transceiver',
    ComputeBay = 'ComputeBay',
    StorageBay = 'StorageBay',
    NetworkBay = 'NetworkBay',
    ExpansionBay = 'ExpansionBay',
    Fan = 'Fan',
    Blade = 'Blade',
    Module = 'Module',
}

export interface HardwareManagementTemperature {
    physical_context: HardwareManagementPhysicalContext | string;
    temperature_celsius: number;
    health: HardwareManagementHealthState;
}

export enum HardwareManagementPowerStateActionType {
    On = 'On',
    ForceOff = 'ForceOff',
    ForceOn = 'ForceOn',
    ForceRestart = 'ForceRestart',
    GracefulShutdown = 'GracefulShutdown',
    GracefulRestart = 'GracefulRestart',
    PushPowerButton = 'PushPowerButton',
    Nmi = 'Nmi',
    PowerCycle = 'PowerCycle',
}

export enum HardwareManagementPowerStateRiskLevel {
    Low = 'low',
    Medium = 'medium',
    High = 'high',
}

export interface HardwareManagementPowerStateAction {
    type: HardwareManagementPowerStateActionType;
    risk_level: HardwareManagementPowerStateRiskLevel;
}

export interface DeviceUsageHardwareManagement {
    device_usage_id: number;
    info: HardwareManagementInfo;
    temperatures: HardwareManagementTemperature[];
    power_state_actions: HardwareManagementPowerStateAction[];
}

export interface RentUsage {
    id: number;
    device_unit_name: string;
    device_name: string;
    order_number: string;
    device_unit_serial: string;
    device_type: string;
}

export interface ColocationUsage {
    id: number;
    rack_name: string;
    rack_room: string;
    order_number: string;
    order_colocation_item_customer_reference: string;
    order_colocation_item_colocation_name: string;
    order_colocation_item_order_feed_item_id: number;
    order_colocation_item_order_feed_item_current_power_consumption: number;
}

export interface CreateNetworkIpValidationError {
    ip?: string[];
    mask?: string[];
    dns_name?: string[];
}

export interface UpdateNetworkIpValidationError {
    ip?: string[];
    mask?: string[];
    dns_name?: string[];
}

export interface TabsItem {
    id: string;
    label: string;
    badge?: string | number;
}

export enum TabsItemTheme {
    Card = 'card',
    Normal = 'normal',
}

export enum CardListSize {
    Small = 'small',
    Normal = 'normal',
}

export enum BadgeSize {
    Small = 'small',
    Normal = 'normal',
    Large = 'large',
}

export interface OrderTrafficUsage {
    graph_svg: string;
}

export interface FluxItem {
    date: string;
    value: number;
}

export interface PowerGraphDataset {
    name: string;
    fluxItems: FluxItem[];
}

export interface OrderFeedItemPowerGraph {
    datasets: PowerGraphDataset[];
    kwh: number | null;
}

export interface OrderTrafficItem {
    id: number;
    category: OrderItemCategory;
    description: string;
    type: OrderItemType;
    items?: unknown[];
    order_traffic_usages: OrderTrafficUsage[];
}

export interface Role {
    name: string;
    permissions: string[];
}

export enum ContactRole {
    Owner = 'owner',
    Admin = 'admin',
    Technician = 'technician',
    Accountant = 'accountant',
}

export interface ContactRoles {
    owner: boolean;
    admin: boolean;
    technician: boolean;
    accountant: boolean;
}

export enum ContactPermission {
    CanManageContacts = 'can_manage_contacts',
    CanManageCustomer = 'can_manage_customer',
    CanAccessReceipts = 'can_access_receipts',
    CanAccessServices = 'can_access_services',
    CanBookDatacenterVisitForSelf = 'can_book_datacenter_visit_for_self',
    CanBookDatacenterVisitForOthers = 'can_book_datacenter_visit_for_others',
    CanSeeAllDatacenterVisits = 'can_see_all_datacenter_visits',
}

export interface ContactPermissions {
    can_manage_contacts: boolean;
    can_manage_customer: boolean;
    can_access_receipts: boolean;
    can_access_services: boolean;
    can_book_datacenter_visit_for_self: boolean;
    can_book_datacenter_visit_for_others: boolean;
    can_see_all_datacenter_visits: boolean;
}

export interface UserFeatures {
    has_serve: boolean;
    has_rent: boolean;
    has_colocation: boolean;
    has_ipam: boolean;
    has_receipt_csv_export: boolean;
    has_two_factor_authentication: boolean;
    has_api_access: boolean;
}

export interface ApiToken {
    id: number;
    user_id: number;
    token_name: string;
    prefix: string;
    last_used_at: string | null;
    created_at: string;
    updated_at: string;
}

export interface UserMetrics {
    open_invoices: number;
    completed_orders: number;
}

export interface NotificationTypePreference {
    enabled: boolean;
    channel_email: boolean;
    channel_inbox: boolean;
}

export interface NotificationTypes {
    miscellaneous: NotificationTypePreference;
    maintenance: NotificationTypePreference;
    price_list: NotificationTypePreference;
    outage: NotificationTypePreference;
    order_start: NotificationTypePreference;
    cancelation: NotificationTypePreference;
    order_approved_by_customer: NotificationTypePreference;
    receipt: NotificationTypePreference;
    invoice_paid: NotificationTypePreference;
    attack_started: NotificationTypePreference;
    attack_ended: NotificationTypePreference;
}

export type NotificationPreferenceMap = Record<NotificationType, NotificationTypePreference>;

export interface User {
    contact_id: number;
    name: string;
    roles: ContactRoles;
    permissions: ContactPermissions;
    available_features: UserFeatures;
    metrics: UserMetrics;
    notification_types: NotificationTypes;
}

export interface UpdateUserPasswordValidationError {
    password_current?: string[];
    password?: string[];
    password_confirmation?: string[];
}

export interface confirmTwoFactorAuthenticationCodeValidationError {
    code?: string[];
}

export interface confirmPasswordCodeValidationError {
    password?: string[];
}

export interface SelectOption {
    key: string;
    label: string;
}

export interface LinkTo {
    id: string;
    label: string;
    url: string;
    to?: RouteLocationNormalized;
    badge?: {
        value: string | null;
        isVisible: boolean;
        theme: Theme;
        title?: string;
    };
}

export enum DisplayType {
    Table = 'table',
    Grid = 'grid',
}

export enum CardType {
    Normal = 'normal',
    Preview = 'Preview',
}

export interface Assortment {
    type: AssortmentType;
    content: unknown;
    last_updated_at: string;
}

export interface AssortmentCloudService {
    name: string;
    cpu_number: number;
    memory: number;
    storage: number;
    iops_read: number;
    hourly_price: string;
}

export enum CloudserverResourceType {
    Cpu = 'cpu',
    Ram = 'ram',
    Storage = 'storage',
}

export enum VpnItemType {
    Ip = 'ip',
    User = 'user',
}

export enum FeedItemIconType {
    GreenCheck = 'green_check',
}

export enum MarketingBannerDisplayLocationType {
    Website = 'website',
    CustomerArea = 'customer_area',
}
export interface MarketingBannerDisplayLocation {
    key: MarketingBannerDisplayLocationType;
    value: MarketingBannerDisplayLocationType;
    label: MarketingBannerDisplayLocationType;
}

export interface MarketingBanner {
    id: string;
    slug: string;
    title: string;
    content: string;
    cta_button: string;
    cta_url: string;
    locale: string;
    display_locations: MarketingBannerDisplayLocation[];
    publish_from: string;
    publish_to: string;
}

export interface SidebarNavItemBadge {
    value: string | null;
    isVisible: boolean;
    theme: BadgeTheme;
    title?: string;
}

export interface SidebarNavItem {
    id: string;
    name: string;
    to: { name: RouteName };
    icon: Component;
    isVisible: boolean;
    badge?: SidebarNavItemBadge;
    subItems?: SidebarNavItem[];
}

export interface SidebarNavigationGroup {
    id: string;
    name: string;
    isVisible: boolean;
    items: SidebarNavItem[];
}

export interface BadgeCarouselItem {
    id: string;
    text: string;
    theme: BadgeTheme;
}

export interface Badge {
    slug: string;
    title: string;
    color: string;
}

export interface NewsPostBadge {
    slug: string;
    title: string;
}

export type NewsPost = {
    id: string;
    locale: string;
    date: string;
    title: string;
    content: string;
    badges: NewsPostBadge[];
};

export enum ContractAttachmentType {
    Pricelist = 'pricelist',
    Agb = 'agb',
    Sla = 'sla',
}

export interface ContractAttachment {
    id: number;
    type: ContractAttachmentType;
    locale: string;
    description: string;
}

export enum RouteName {
    DashboardPage = 'DashboardPage',
    InvoiceIndexPage = 'InvoiceIndexPage',
    InvoicePaymentPage = 'InvoicePaymentPage',
    InvoicePaymentSuccessPage = 'InvoicePaymentSuccessPage',
    DeviceUsageIndexPage = 'DeviceUsageIndexPage',
    DeviceUsagePage = 'DeviceUsagePage',
    ColocationUsageIndexPage = 'ColocationUsageIndexPage',
    RentUsageIndexPage = 'RentUsageIndexPage',
    SettingsAccountPage = 'SettingsAccountPage',
    OrderIndexPage = 'OrderIndexPage',
    OrderRouterViewPage = 'OrderRouterViewPage',
    OrderPage = 'OrderPage',
    OrderWanPage = 'OrderWanPage',
    OrderLanPage = 'OrderLanPage',
    OrderPowerPage = 'OrderPowerPage',
    TicketIndexPage = 'TicketIndexPage',
    TicketRouterViewPage = 'TicketRouterViewPage',
    TicketPage = 'TicketPage',
    IpUsageIndexPage = 'IpUsageIndexPage',
    IpUsageNetworkPage = 'IpUsageNetworkPage',
    AttackRouterViewPage = 'AttackRouterViewPage',
    AttackIndexPage = 'AttackIndexPage',
    AttackPage = 'AttackPage',
    NewsPostIndexPage = 'NewsPostIndexPage',
    CustomerPage = 'CustomerPage',
    ContactRouterViewPage = 'ContactRouterViewPage',
    ContactPage = 'ContactPage',
    DatacenterVisitIndexPage = 'DatacenterVisitIndexPage',
    DatacenterVisitWizardPage = 'DatacenterVisitWizardPage',
    InboxPage = 'InboxPage',
    StorybookPage = 'StorybookPage',
}

export interface NotificationGroup {
    id: string;
    labelKey: string;
    notificationTypes: NotificationType[];
    order: number;
    source: 'local' | 'backend';
}

export interface InboxFilterOption {
    id: string;
    label?: string;
    icon?: Component;
    count?: number | null;
    isSeparator?: boolean;
}

export interface InboxNotificationContentData {
    subject: string;
    subject_i18n: string;
    salutation: string;
    salutation_i18n: string;
    content: string;
    content_i18n: string;
    attachments: string[];
}

export interface InboxNotificationAttachment {
    id: string;
    name: string;
    mime_type?: string;
    size?: number;
    created_at?: string;
}

export interface InboxNotificationPayload {
    type: NotificationType;
    data: InboxNotificationContentData;
    created_at: string;
    campaign_id: string | null;
    attachments: InboxNotificationAttachment[];
}

export interface InboxNotification {
    id: string;
    notification_id: string;
    read_at: string | null;
    created_at: string;
    notification: InboxNotificationPayload;
}

export enum AttackStatus {
    Start = 'start',
    Ongoing = 'ongoing',
    End = 'end',
}
export interface Attack {
    attack_id: string;
    tenant_id: number;
    ip_address: string;
    status: AttackStatus;
    date: string;
    duration: number;
    peak: number;
    volume: number;
    units: string;
    description: string;
    swa: string;
    details?: AttackDetails;
}

export interface AttackDetails {
    source_ip: AttackDetailsEntry[];
    source_country: AttackDetailsEntry[];
    source_asn: AttackDetailsEntry[];
    source_port: AttackDetailsEntry[];
    destination_port: AttackDetailsEntry[];
    packet_length: AttackDetailsEntry[];
    protocol: AttackDetailsEntry[];
}

export interface AttackDetailsEntry {
    value: string;
    percentage: number;
}

export interface AttackTrafficDataPoint {
    date: string;
    allowedMbps: number;
    blockedMbps: number;
}

export type TimeRangeUnit = 'hours' | 'days' | 'months';

export interface TimeRangeOption {
    value: number;
    unit: TimeRangeUnit;
    label?: string;
}

export type TimeRangeSelection = TimeRangeOption | 'custom';

export interface AttackFilters {
    timeRange: TimeRangeSelection;
    from: string;
    to: string;
    ip_address: string;
}

export interface AttackRequestFilters {
    from: string;
    to: string;
    ip_address: string;
}

// Datacenter Visit Wizard Types
export interface DatacenterVisitCompanyUser {
    id: number;
    name: string;
    email: string;
}

export interface DatacenterVisitSelectedUser {
    id: number;
    name: string;
    displayName: string;
}

export interface DatacenterVisitRackOption {
    id: string;
    label: string;
    rack_name: string;
    rack_room: string;
    customer_reference: string;
    colocation_name: string;
    hasExtraCost: boolean;
    needsUnlocking: boolean;
}

export interface DatacenterVisitFormData {
    requesting_user?: string;
    selectedUsers: DatacenterVisitSelectedUser[];
    guests: string[];
    startDate: string;
    endDate: string;
    selectedRacks: string[];
    rackUnlockSettings: Record<string, boolean>;
    additionalInfo: string;
    isNocTicket: boolean;
    termsAccepted: boolean;
}

export interface DatacenterVisitTicketItem {
    id: number;
    helpdesk_id: number;
}

export interface DatacenterVisit {
    id: number;
    contact_id: number;
    customer_number: string;
    visit_start: string;
    visit_end: string;
    visitor_names: string[];
    rack_names: string[];
    notes: string | null;
    is_noc: boolean;
    created_at: string;
    helpdesk_items?: DatacenterVisitTicketItem[];
    ticket?: Ticket | null;
}

export interface CreateDatacenterVisitRequest {
    contact_id: number;
    customer_number: string;
    helpdesk_id: number;
    visit_start: string;
    visit_end: string;
    visitor_names: string[];
    rack_names: string[];
    notes?: string | null;
    is_noc: boolean;
}

export interface CreateDatacenterVisitParams {
    contact_id: number;
    customer_number: string;
    helpdesk_id: number;
    visit_start: string;
    visit_end: string;
    selectedUsers: DatacenterVisitSelectedUser[];
    guests: string[];
    rack_names: string[];
    notes: string;
    is_noc: boolean;
}

export interface Holiday {
    name: string;
    date: string;
    type: string[];
    states: string;
}

export interface NocValidationCriteria {
    requestDateTime: Date;
}

export interface NocValidationOutcome {
    isNocRequired: boolean;
    reasons: string[];
    reasonIds: string[];
}

export interface DatacenterVisitNocValidation {
    criteria: NocValidationCriteria;
    outcome: NocValidationOutcome;
    businessHours: TimeRange;
}

export interface NocStatus {
    outcome: NocValidationOutcome;
    businessHours: TimeRange;
}

export interface TimeRange {
    start: string;
    end: string;
}

export interface DayBusinessHours {
    [key: number]: TimeRange;
}

export interface NocValidationCoreResult {
    isNocRequired: boolean;
    reasons: string[];
    reasonIds: string[];
}

export interface BaseWizardState {
    step: Ref<number>;
    isWizardCompleted: Ref<boolean>;
    isResetting: Ref<boolean>;
    maxSteps: Ref<number>;
    hasReachedSummaryStep: Ref<boolean>;
}

export interface BaseWizardApi {
    goToNextStep: () => void;
    goToPreviousStep: () => void;
    goToStep: (stepNumber: number) => void;
    resetWizard: () => void;
    handleWizardCompletion: () => void;
    getStepIndex: (stepNumber: number) => number;
    getTotalVisibleSteps: () => number;
    isStepDisabled: (stepNumber: number) => boolean;
}

export interface BaseWizardContext {
    state: BaseWizardState;
    api: BaseWizardApi;
    stepsProgress: ComputedRef<Record<number, StepStatus>>;
}

export interface DatacenterVisitWizardStepMapping {
    dateTime: number;
    visitors: number;
    guests: number;
    rackAccess: number;
    yourMessage: number;
    summary: number;
}

export interface DatacenterVisitWizardState extends BaseWizardState {
    currentStepIsValid: Ref<boolean>;
    requestingUser: Ref<string>;
    userName: Ref<string>;
    selectedUsers: Ref<DatacenterVisitSelectedUser[]>;
    guests: Ref<string[]>;

    startDate: Ref<string>;
    endDate: Ref<string>;
    selectedRacks: Ref<string[]>;
    rackUnlockSettings: Ref<Record<string, boolean>>;
    additionalInfo: Ref<string>;

    isNocTicket: Ref<boolean>;
    termsAccepted: Ref<boolean>;
    nocStatus: Ref<NocStatus>;

    createdTicketId: Ref<number | null>;

    hasColocationUsages: Ref<boolean | null>;

    stepMapping: ComputedRef<DatacenterVisitWizardStepMapping>;
}

export interface DatacenterVisitWizardApi extends BaseWizardApi {
    setCurrentStepValidity: (isValid: boolean) => void;
    updateNocStatus: () => Promise<void>;

    setupWizard: () => Promise<void>;

    getVisibleSteps: () => number[];
}

export interface DatacenterVisitWizardContext {
    state: DatacenterVisitWizardState;
    api: DatacenterVisitWizardApi;
    stepsProgress: ComputedRef<Record<number, StepStatus>>;
    stores: {
        userStore: ReturnType<typeof import('@/modules/your/stores/userStore').useUserStore>;
        contactStore: ReturnType<
            typeof import('@/modules/your/stores/contactStore').useContactStore
        >;
        colocationUsageStore: ReturnType<
            typeof import('@/modules/your/stores/colocationUsageStore').useColocationUsageStore
        >;
        customerStore: ReturnType<
            typeof import('@/modules/your/stores/customerStore').useCustomerStore
        >;
    };
}

export interface PaymentIntent {
    id: string;
    amount: number;
    currency: string;
    status: string;
    client_secret: string;
    invoice_number: string;
    customer_number: number;
    contact_email: string;
    created_at: string;
}

export enum AvailableLocale {
    DE = 'de',
    EN = 'en',
}

export interface PaymentResponse extends ObjectResponse {
    payment_intent: PaymentIntent;
}

export interface CreatePaymentIntentRequest {
    amount: number;
    currency: string;
    invoice_number: string;
    payment_method_types?: string[];
    metadata?: Record<string, string>;
    description?: string;
    receipt_email?: string;
    automatic_payment_methods?: boolean;
}

export enum PaymentStatus {
    Idle = 'idle',
    Processing = 'processing',
    Success = 'success',
    Error = 'error',
}
